import { useIonViewWillEnter } from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Lead,
  type LeadEventWithStatusDto,
  type LeadScopeCountsDto,
  LeadListScope,
  LeadQuerySortBy,
  LeadsApi,
  type LeadStatus,
  Permission,
  QuerySortDirection,
  LeadEventLifecycleStatus,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { invalidateStatusBoardQueries } from '@/core/components/dashboard/dashboard-refresh-keys';
import { RESOURCE_JUST_CREATED_HIGHLIGHT_MS } from '@/core/components/layout/list-toolbar-styles';
import type { LeadCardMetaLines } from '@/core/components/leads/lead-card-content';
import { getMemberDisplayName } from '@/core/components/leads/lead-detail/lead-user-display';
import {
  applyLeadsListFilters,
  collectDistinctLeadTags,
  countActiveLeadsListFilters,
  EMPTY_LEADS_LIST_FILTERS,
  LEAD_FILTER_ANONYMOUS_CREATOR_ID,
  LEAD_FILTER_UNASSIGNED_ID,
  leadNormalizedAssigneeFilterId,
  leadNormalizedCreatorFilterId,
} from '@/core/components/leads/lead-filter-normalize';
import {
  buildAssigneeFilterOptions,
  buildEventFilterOptions,
  buildLeadEventMaps,
} from '@/core/components/leads/leads-page-filter-options';
import {
  buildLeadsListSearch,
  leadsStatusSearchKey,
  parseLeadsQueryFromSearch,
  parseLeadsScopeFromSearch,
  parseLeadsStatusFromSearch,
  parseLeadsViewFromSearch,
  type LeadsView,
} from '@/core/components/leads/leads-list-filter-url';
import {
  readLeadsViewPreference,
  writeLeadsViewPreference,
} from '@/core/components/leads/leads-view-preference';
import type { LeadsListFilterValues } from '@/core/components/leads/leads-list-filters';
import {
  type LeadsListSortOption,
  sortLeadsForList,
} from '@/core/components/leads/leads-list-sort';
import {
  canSeeOthersPrivateLeadsScope,
  defaultLeadsListScope,
  leadsScopeShowsNewOwnLead,
} from '@/core/components/leads/leads-scope-access';
import type { LeadsStatusCounts } from '@/core/components/leads/leads-status-pills';
import {
  LEAD_STATUS_IDS,
  isLeadStatus,
} from '@/core/components/leads/lead-status-colors';
import { LEADS_LIST_SCOPE_KEY } from '@/core/constants/leads-storage';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useMyMemberships } from '@/core/hooks/use-my-memberships';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useProfile } from '@/core/hooks/use-profile';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { downloadCsvFile } from '@/core/lib/download-csv';
import { isLeadArchived } from '@/core/lib/lead-mutate-access';
import {
  buildEventNameMap,
  eventNamesFromLead,
} from '@/core/lib/lead-event-names';
import { fetchAllLeadEventsPages } from '@/core/lib/lead-events-fetch-all';
import { exportLeadsCsv, leadsCsvFilename } from '@/core/lib/leads-csv';
import { fetchAllLeadsWithMeta } from '@/core/lib/leads-fetch-all';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import {
  leadEventsQueryKeys,
  leadsQueryKeys,
} from '@/core/query-keys/leads-query-keys';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

const EMPTY_LEADS_ITEMS: Lead[] = [];
const EMPTY_SCOPE_COUNTS: LeadScopeCountsDto = {
  mine: 0,
  workspace: 0,
  othersPrivate: 0,
  archivedHidden: 0,
};

export function useLeadsPage() {
  const dict = useDictionary();
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateShell } = useNavigateShell();
  const { profile, isLoading: profileLoading } = useProfile();
  const currentWorkspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { memberships, isLoading: membershipsLoading } = useMyMemberships();
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const canReadLeads = can(Permission.LEADS_READ);
  const canExportLeads = can(Permission.LEADS_EXPORT);
  const canUpdateLeadsBase = can(Permission.LEADS_UPDATE);
  const canUpdateOthersPrivate = can(Permission.LEADS_UPDATE_OTHERS);
  const eventsEnabled = isFeatureEnabled('feature.lead_events');
  const workspaceLeadsAlwaysShared =
    currentWorkspace?.leadsAlwaysSharedWithWorkspace === true;
  const membershipPermissions = useMemo(() => {
    if (!currentWorkspace) return undefined;
    return memberships.find(
      (entry) => entry.workspace?.id === currentWorkspace.id,
    )?.permissions;
  }, [currentWorkspace, memberships]);
  const canSeeOthersPrivate = canSeeOthersPrivateLeadsScope({
    permissions: membershipPermissions,
    permissionsReady: !membershipsLoading && !!currentWorkspace,
    leadsAlwaysShared: workspaceLeadsAlwaysShared,
  });

  // null = no explicit choice yet; resolved by defaultLeadsListScope below.
  const [chosenListScope, setListScopeState] = useState<LeadListScope | null>(
    () => {
      const fromUrl = parseLeadsScopeFromSearch(
        typeof window !== 'undefined' ? window.location.search : '',
      );
      if (fromUrl) return fromUrl;
      if (typeof window === 'undefined') return null;
      const raw = sessionStorage.getItem(LEADS_LIST_SCOPE_KEY);
      if (raw === LeadListScope.MINE) return LeadListScope.MINE;
      if (raw === LeadListScope.WORKSPACE) return LeadListScope.WORKSPACE;
      if (raw === LeadListScope.OTHERS_PRIVATE)
        return LeadListScope.OTHERS_PRIVATE;
      return null;
    },
  );
  const listScope =
    chosenListScope ??
    defaultLeadsListScope({ leadsAlwaysShared: workspaceLeadsAlwaysShared });

  const [searchQuery, setSearchQueryState] = useState(() =>
    parseLeadsQueryFromSearch(location.search),
  );
  const [includeArchived, setIncludeArchived] = useState(false);
  const [listSort, setListSort] = useState<LeadsListSortOption>('createdDesc');
  const [leadFilters, setLeadFilters] = useState<LeadsListFilterValues>(() => ({
    ...EMPTY_LEADS_LIST_FILTERS,
    statuses: parseLeadsStatusFromSearch(location.search),
  }));
  const [appliedStatusSearch, setAppliedStatusSearch] = useState(
    location.search,
  );
  if (location.search !== appliedStatusSearch) {
    setAppliedStatusSearch(location.search);
    const statuses = parseLeadsStatusFromSearch(location.search);
    const qFromUrl = parseLeadsQueryFromSearch(location.search);
    const scopeFromUrl = parseLeadsScopeFromSearch(location.search);
    setSearchQueryState(qFromUrl);
    if (scopeFromUrl) {
      setListScopeState(scopeFromUrl);
    }
    setLeadFilters((prev) => {
      if (
        leadsStatusSearchKey(prev.statuses) === leadsStatusSearchKey(statuses)
      ) {
        return prev;
      }
      return { ...prev, statuses };
    });
  }
  const [isExporting, setIsExporting] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectionWorkspaceId, setSelectionWorkspaceId] = useState<
    string | null
  >(currentWorkspace?.id ?? null);

  // Reset bulk selection when switching workspace (adjust during render; avoid setState-in-effect).
  const workspaceIdForSelection = currentWorkspace?.id ?? null;
  if (workspaceIdForSelection !== selectionWorkspaceId) {
    setSelectionWorkspaceId(workspaceIdForSelection);
    setSelectedLeadIds(new Set());
  }

  const syncListUrl = useCallback(
    (next: { scope?: LeadListScope; q?: string }) => {
      const search = buildLeadsListSearch({
        currentSearch: location.search,
        scope: next.scope,
        q: next.q,
        status: leadFilters.statuses,
      });
      if (search !== location.search) {
        navigate({ pathname: location.pathname, search }, { replace: true });
      }
    },
    [leadFilters.statuses, location.pathname, location.search, navigate],
  );

  const setListScope = useCallback(
    (scope: LeadListScope) => {
      if (scope === LeadListScope.OTHERS_PRIVATE && !canSeeOthersPrivate) {
        return;
      }
      setListScopeState(scope);
      sessionStorage.setItem(LEADS_LIST_SCOPE_KEY, scope);
      syncListUrl({ scope, q: searchQuery });
    },
    [canSeeOthersPrivate, searchQuery, syncListUrl],
  );

  const setSearchQuery = useCallback(
    (value: string) => {
      setSearchQueryState(value);
      syncListUrl({ scope: listScope, q: value });
    },
    [listScope, syncListUrl],
  );

  /** Without READ_OTHERS, Privat anderer falls back without an effect. */
  const effectiveListScope =
    listScope === LeadListScope.OTHERS_PRIVATE && !canSeeOthersPrivate
      ? LeadListScope.WORKSPACE
      : listScope;

  const canUpdateLeads =
    effectiveListScope === LeadListScope.OTHERS_PRIVATE
      ? canUpdateLeadsBase && canUpdateOthersPrivate
      : canUpdateLeadsBase;

  // URL first (shared links), then this device's last choice (DEV-567).
  const [activeTab, setActiveTabState] = useState<LeadsView>(
    () =>
      parseLeadsViewFromSearch(
        typeof window !== 'undefined' ? window.location.search : '',
      ) ??
      readLeadsViewPreference() ??
      'list',
  );

  const [businessCardScanOpen, setBusinessCardScanOpen] = useState(false);
  const [createRequestId, setCreateRequestId] = useState(0);
  /** Board column "+ Lead" pre-selects its status; other CTAs create as Neu. */
  const [createInitialStatus, setCreateInitialStatus] = useState<
    LeadStatus | undefined
  >(undefined);
  const requestCreateLead = useCallback((status?: LeadStatus) => {
    setCreateInitialStatus(status);
    setCreateRequestId((n) => n + 1);
  }, []);
  const [searchRequestId, setSearchRequestId] = useState(0);
  const requestSearch = useCallback(() => {
    setSearchRequestId((n) => n + 1);
  }, []);
  /**
   * Each tab mounts its own create form / search field, which open for any
   * request id they have not seen — reset so the incoming tab does not replay
   * the previous tab's last create or search request.
   */
  const setActiveTab = useCallback(
    (tab: LeadsView) => {
      if (tab === activeTab) return;
      setCreateRequestId(0);
      setCreateInitialStatus(undefined);
      setSearchRequestId(0);
      setActiveTabState(tab);
      writeLeadsViewPreference(tab);
      const search = buildLeadsListSearch({
        currentSearch: location.search,
        view: tab,
      });
      if (search !== location.search) {
        navigate({ pathname: location.pathname, search }, { replace: true });
      }
    },
    [activeTab, location.pathname, location.search, navigate],
  );
  const [highlightedLeadId, setHighlightedLeadId] = useState<string | null>(
    null,
  );
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const workspaceIdForLeads = currentWorkspace?.id;
  const queryKey = useMemo(
    () =>
      workspaceIdForLeads
        ? ([
            ...leadsQueryKeys.list(workspaceIdForLeads, effectiveListScope),
            searchQuery.trim(),
            includeArchived,
          ] as const)
        : leadsQueryKeys.root,
    [workspaceIdForLeads, effectiveListScope, searchQuery, includeArchived],
  );

  const refreshLeads = useCallback(async () => {
    const tasks = [
      queryClient.invalidateQueries({ queryKey }),
      invalidateStatusBoardQueries(queryClient),
    ];
    if (eventsEnabled && workspaceIdForLeads) {
      tasks.push(
        queryClient.invalidateQueries({
          queryKey: leadEventsQueryKeys.all(workspaceIdForLeads),
        }),
      );
    }
    await Promise.all(tasks);
  }, [eventsEnabled, queryClient, queryKey, workspaceIdForLeads]);

  useIonViewWillEnter(() => {
    void refreshLeads();
  });
  const {
    data: leadsQueryData,
    isLoading,
    error: leadsError,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<{
      items: Lead[];
      scopeCounts: LeadScopeCountsDto;
    }> => {
      if (!currentWorkspace?.id) {
        return {
          items: [],
          scopeCounts: {
            mine: 0,
            workspace: 0,
            othersPrivate: 0,
            archivedHidden: 0,
          },
        };
      }
      return fetchAllLeadsWithMeta(currentWorkspace.id, {
        includeArchived,
        sortBy: LeadQuerySortBy.ORDER,
        sortOrder: QuerySortDirection.ASC,
        scope: effectiveListScope,
        ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
      });
    },
    // `withRequiredPermissions` keeps the page mounted while it redirects, so
    // skip the fetch instead of firing a request the API will reject.
    enabled: !profileLoading && !!currentWorkspace?.id && canReadLeads,
    staleTime: 30 * 1000,
    retry: false,
  });

  const leads = useMemo(
    () => leadsQueryData?.items ?? EMPTY_LEADS_ITEMS,
    [leadsQueryData?.items],
  );
  const scopeCounts = leadsQueryData?.scopeCounts ?? EMPTY_SCOPE_COUNTS;

  const { data: workspaceMembers = [] } = useWorkspaceMembersRoster(
    currentWorkspace?.id,
    'leads-filters',
    { staleTime: 60 * 1000 },
  );

  const { data: leadEvents = [] } = useQuery({
    queryKey: currentWorkspace?.id
      ? leadEventsQueryKeys.all(currentWorkspace.id)
      : leadEventsQueryKeys.root,
    queryFn: async () => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) return [];
      return fetchAllLeadEventsPages(workspaceId);
    },
    enabled: !!currentWorkspace?.id && eventsEnabled && !entitlementsLoading,
    staleTime: 30_000,
    retry: false,
  });

  const memberLabelByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of workspaceMembers) {
      const uid = m.userId?.trim();
      if (!uid) continue;
      const label = getMemberDisplayName(m);
      if (label) map.set(uid, label);
    }
    return map;
  }, [workspaceMembers]);

  const collatorLocale = locale === 'de' ? 'de' : 'en';

  const assigneeFilterOptions = useMemo(
    () =>
      buildAssigneeFilterOptions({
        leads: leads,
        memberLabelByUserId,
        unknownAuthorLabel: dict.leads.detail.activityUnknownAuthor,
        unassignedLabel: dict.leads.detail.assigneePicker?.nobody ?? '—',
        collatorLocale,
      }),
    [
      leads,
      memberLabelByUserId,
      dict.leads.detail.activityUnknownAuthor,
      dict.leads.detail.assigneePicker?.nobody,
      collatorLocale,
    ],
  );

  const eventStatusLabels = dict.leads.events?.status as
    | Record<string, string>
    | undefined;

  const eventFilterOptions = useMemo(
    () =>
      buildEventFilterOptions({
        eventsEnabled,
        leadEvents,
        collatorLocale,
        eventStatusLabels,
      }),
    [eventsEnabled, leadEvents, collatorLocale, eventStatusLabels],
  );

  const {
    eventNameById,
    leadCardLabelMaps,
    tagFilterOptions,
    legacyEventTagExclusions,
  } = useMemo(
    () =>
      buildLeadEventMaps({
        eventsEnabled,
        leadEvents,
        leads,
        collatorLocale,
      }),
    [eventsEnabled, leadEvents, leads, collatorLocale],
  );

  const resolveLeadCardMeta = useCallback(
    (lead: Lead): LeadCardMetaLines | null => {
      const unknownAuthor = dict.leads.detail.activityUnknownAuthor;
      const creatorId = leadNormalizedCreatorFilterId(lead);
      const creatorName =
        creatorId === LEAD_FILTER_ANONYMOUS_CREATOR_ID
          ? (memberLabelByUserId.get(lead.userId?.trim() ?? '') ??
            dict.leads.list.filters.anonymousCreatorLabel)
          : (memberLabelByUserId.get(creatorId) ?? unknownAuthor);
      const assigneeId = leadNormalizedAssigneeFilterId(lead);
      const assigneeName =
        assigneeId === LEAD_FILTER_UNASSIGNED_ID
          ? (dict.leads.detail.assigneePicker?.nobody ?? '—')
          : (memberLabelByUserId.get(assigneeId) ?? unknownAuthor);
      const src =
        (dict.leads.list.source as Record<string, string>)?.[lead.source] ??
        lead.source;
      const cm = dict.leads.list.cardMeta;
      let visibility: 'shared' | 'private' | null = null;
      if (!workspaceLeadsAlwaysShared) {
        visibility = lead.visibility === 'workspace' ? 'shared' : 'private';
      }
      return {
        creatorLabel: cm.creator,
        creatorValue: creatorName,
        assigneeLabel: cm.assignee,
        assigneeValue: assigneeName,
        sourceLabel: cm.source,
        sourceValue: src,
        visibility,
        visibilitySharedLabel: cm.visibilityShared,
        visibilityPrivateLabel: cm.visibilityPrivate,
      };
    },
    [
      dict.leads.detail.activityUnknownAuthor,
      dict.leads.detail.assigneePicker?.nobody,
      dict.leads.list.filters.anonymousCreatorLabel,
      dict.leads.list.cardMeta,
      dict.leads.list.source,
      memberLabelByUserId,
      workspaceLeadsAlwaysShared,
    ],
  );

  /** Drop legacy Event auto-tags from the Tags filter without syncing via an effect. */
  const effectiveLeadFilters = useMemo(() => {
    if (!eventsEnabled || legacyEventTagExclusions.length === 0) {
      return leadFilters;
    }
    const excluded = new Set(
      legacyEventTagExclusions.map((t) => t.toLowerCase()),
    );
    const nextTags = leadFilters.tags.filter(
      (tag) => !excluded.has(tag.trim().toLowerCase()),
    );
    if (nextTags.length === leadFilters.tags.length) return leadFilters;
    return { ...leadFilters, tags: nextTags };
  }, [leadFilters, eventsEnabled, legacyEventTagExclusions]);

  const hasActiveLeadFilters =
    countActiveLeadsListFilters(effectiveLeadFilters) > 0;

  const filterLabels = useMemo(
    () => ({
      filter: dict.leads.list.filter,
      filtersTitle: dict.leads.list.filters.title,
      clearAll: dict.leads.list.filters.clearAll,
      done: dict.leads.list.filters.done,
      sectionSource: dict.leads.list.filters.sectionSource,
      sectionTags: dict.leads.list.filters.sectionTags,
      noTagsToFilter: dict.leads.list.filters.noTagsToFilter,
      sectionPeriod: dict.leads.list.filters.sectionPeriod,
      periodAll: dict.leads.list.filters.periodAll,
      periodLastDay: dict.leads.list.filters.periodLastDay,
      periodLastWeek: dict.leads.list.filters.periodLastWeek,
      periodLastMonth: dict.leads.list.filters.periodLastMonth,
      sourceLabels: dict.leads.list.source as Record<string, string>,
    }),
    [dict],
  );

  const mobileFilterLabels = useMemo(() => {
    const filters = dict.leads.list.filters;
    const sortChips = dict.leads.list.sortChips;
    return {
      filter: dict.leads.list.filter,
      cancel: filters.cancel,
      reset: filters.reset,
      sectionSort: filters.sectionSort,
      sectionFilters: filters.sectionFilters,
      allValues: filters.allValues,
      showResults: filters.showResults,
      showResultsNone: filters.showResultsNone,
      searchEvent: filters.searchEvent,
      searchAssignee: filters.searchAssignee,
      searchTag: filters.searchTag,
      sectionEvents: filters.sectionEvents,
      sectionAssignee: filters.sectionAssignee,
      sectionTags: filters.sectionTags,
      sectionSource: filters.sectionSource,
      sectionPeriod: filters.sectionPeriod,
      showArchived: filters.showArchived,
      noEventsToFilter: filters.noEventsToFilter,
      noPeopleToFilter: filters.noPeopleToFilter,
      noTagsToFilter: filters.noTagsToFilter,
      periodAll: filters.periodAll,
      periodLastDay: filters.periodLastDay,
      periodLastWeek: filters.periodLastWeek,
      periodLastMonth: filters.periodLastMonth,
      sourceLabels: dict.leads.list.source as Record<string, string>,
      sortChipLabels: sortChips,
      removeFilter: filters.removeFilter,
    };
  }, [dict]);

  const sortLabels = {
    sort: dict.leads.list.sort,
    options: dict.leads.list.sortOptions,
  };

  const filteredLeads = useMemo(() => {
    const list = applyLeadsListFilters(
      leads,
      effectiveLeadFilters,
      searchQuery,
    );
    return sortLeadsForList(list, listSort, collatorLocale);
  }, [leads, searchQuery, effectiveLeadFilters, listSort, collatorLocale]);

  /** Status pill counts ignore the status facet so chips stay stable while filtering. */
  const statusCounts = useMemo((): LeadsStatusCounts => {
    const withoutStatus = applyLeadsListFilters(
      leads,
      { ...effectiveLeadFilters, statuses: [] },
      searchQuery,
    );
    const counts = {
      all: withoutStatus.length,
      ...Object.fromEntries(LEAD_STATUS_IDS.map((id) => [id, 0])),
    } as LeadsStatusCounts;
    for (const lead of withoutStatus) {
      if (isLeadStatus(lead.status)) {
        counts[lead.status] += 1;
      }
    }
    return counts;
  }, [leads, effectiveLeadFilters, searchQuery]);

  /** Keep selection across bulk actions; ignore ids outside the current filtered view. */
  const activeSelectedLeadIds = useMemo(() => {
    if (selectedLeadIds.size === 0) return selectedLeadIds;
    const visible = new Set(filteredLeads.map((lead) => lead.id));
    let changed = false;
    const next = new Set<string>();
    for (const id of selectedLeadIds) {
      if (visible.has(id)) next.add(id);
      else changed = true;
    }
    return changed ? next : selectedLeadIds;
  }, [filteredLeads, selectedLeadIds]);

  const selectedLeads = useMemo(
    () => filteredLeads.filter((lead) => activeSelectedLeadIds.has(lead.id)),
    [filteredLeads, activeSelectedLeadIds],
  );

  const allFilteredSelected =
    filteredLeads.length > 0 &&
    filteredLeads.every((lead) => activeSelectedLeadIds.has(lead.id));
  const someFilteredSelected =
    !allFilteredSelected &&
    filteredLeads.some((lead) => activeSelectedLeadIds.has(lead.id));

  const removableTagsForSelection = useMemo(() => {
    if (selectedLeads.length === 0) return [];
    const tagSets = selectedLeads.map(
      (lead) => new Set((lead.tags ?? []).map((t) => t.trim()).filter(Boolean)),
    );
    const intersection = [...tagSets[0]!].filter((tag) =>
      tagSets.every((set) => set.has(tag)),
    );
    return intersection.sort((a, b) =>
      a.localeCompare(b, collatorLocale, { sensitivity: 'base' }),
    );
  }, [selectedLeads, collatorLocale]);

  const bulkEventOptions = useMemo(
    () =>
      (leadEvents as LeadEventWithStatusDto[]).map((ev) => ({
        id: ev.id,
        label: ev.name,
      })),
    [leadEvents],
  );

  const removableEventIdsForSelection = useMemo(() => {
    if (selectedLeads.length === 0) return [];
    const eventSets = selectedLeads.map(
      (lead) =>
        new Set((lead.eventIds ?? []).map((id) => id.trim()).filter(Boolean)),
    );
    return [...eventSets[0]!].filter((id) =>
      eventSets.every((set) => set.has(id)),
    );
  }, [selectedLeads]);

  const bulkAssignmentCountsByUserId = useMemo(() => {
    const map = new Map<string, number>();
    for (const lead of selectedLeads) {
      const aid = lead.assigneeUserId?.trim() ?? '';
      if (!aid) continue; // unassigned (Niemand) — do not count as owner
      map.set(aid, (map.get(aid) ?? 0) + 1);
    }
    return map;
  }, [selectedLeads]);

  const bulkPrivateShareCount = useMemo(() => {
    if (workspaceLeadsAlwaysShared) return 0;
    return selectedLeads.filter((lead) => lead.visibility === 'private').length;
  }, [selectedLeads, workspaceLeadsAlwaysShared]);

  const bulkDict = dict.leads.list.bulk;

  const runExportLeads = useCallback(
    (leadsToExport: Lead[]) => {
      if (!canExportLeads || leadsToExport.length === 0 || isExporting) return;
      setIsExporting(true);
      try {
        const csvDict = dict.leads.list.csv;
        const csv = exportLeadsCsv(leadsToExport, {
          eventNameById,
          headers: [
            csvDict.firstName,
            csvDict.lastName,
            csvDict.email,
            csvDict.phone,
            csvDict.company,
            csvDict.address,
            csvDict.jobTitle,
            csvDict.status,
            csvDict.source,
            csvDict.tags,
            csvDict.events,
            csvDict.assignee,
            csvDict.createdAt,
            csvDict.notes,
            csvDict.message,
          ],
          statusLabels: dict.leads.status,
          sourceLabels: dict.leads.list.source,
          assigneeNameById: Object.fromEntries(
            workspaceMembers
              .filter((m) => !!m.userId?.trim())
              .map((m) => [
                m.userId!.trim(),
                getMemberDisplayName(m) || m.userId!.trim(),
              ]),
          ),
          unassignedLabel: dict.leads.detail.assigneePicker?.nobody ?? '',
          locale,
        });
        const filename = leadsCsvFilename(
          currentWorkspace?.username ?? currentWorkspace?.name ?? 'workspace',
        );
        void downloadCsvFile(csv, filename)
          .catch((error: unknown) => {
            console.error('Leads CSV export failed', error);
            toast.error(dict.leads.list.exportError);
          })
          .finally(() => {
            setIsExporting(false);
          });
      } catch (error: unknown) {
        console.error('Leads CSV export failed', error);
        toast.error(dict.leads.list.exportError);
        setIsExporting(false);
      }
    },
    [
      canExportLeads,
      isExporting,
      eventNameById,
      workspaceMembers,
      locale,
      dict.leads.list.csv,
      dict.leads.status,
      dict.leads.list.source,
      dict.leads.detail.assigneePicker,
      currentWorkspace,
      dict.leads.list.exportError,
    ],
  );

  const handleExportCsv = useCallback(() => {
    runExportLeads(filteredLeads);
  }, [runExportLeads, filteredLeads]);

  const handleExportSelected = useCallback(() => {
    runExportLeads(selectedLeads);
  }, [runExportLeads, selectedLeads]);

  const bulkUpdateMutation = useMutation({
    mutationFn: async (body: {
      setStatus?: LeadStatus;
      setAssigneeUserId?: string | null;
      addTags?: string[];
      removeTags?: string[];
      addEventIds?: string[];
      removeEventIds?: string[];
    }) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('workspaceId required');
      }
      const leadIds = [...activeSelectedLeadIds];
      if (leadIds.length === 0) {
        throw new Error('no leads selected');
      }
      const response = await LeadsApi.bulkUpdate({
        path: { workspaceId },
        body: {
          leadIds,
          ...body,
        },
      });
      return response.data ?? [];
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void invalidateStatusBoardQueries(queryClient);
    },
    onError: (error: unknown) => {
      console.error('Leads bulk update failed', error);
      toast.error(bulkDict.updateError);
    },
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: async (archived: boolean) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('workspaceId required');
      }
      const selected = filteredLeads.filter((lead) =>
        activeSelectedLeadIds.has(lead.id),
      );
      const leadIds = selected
        .filter((lead) =>
          archived ? !isLeadArchived(lead) : isLeadArchived(lead),
        )
        .map((lead) => lead.id);
      if (leadIds.length === 0) {
        return;
      }
      await Promise.all(
        leadIds.map((id) =>
          LeadsApi.update({
            path: { workspaceId, id },
            body: { archived },
          }),
        ),
      );
    },
    onSuccess: () => {
      setSelectedLeadIds(new Set());
      void queryClient.invalidateQueries({ queryKey });
      void invalidateStatusBoardQueries(queryClient);
    },
    onError: (error: unknown, archived: boolean) => {
      console.error(
        archived ? 'Leads bulk archive failed' : 'Leads bulk unarchive failed',
        error,
      );
      toast.error(
        archived
          ? (dict.leads.list.contextMenu?.archiveError ?? bulkDict.updateError)
          : (dict.leads.list.contextMenu?.unarchiveError ??
              bulkDict.updateError),
      );
    },
  });

  const toggleLeadSelect = useCallback((leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedLeadIds((prev) => {
      const allSelected =
        filteredLeads.length > 0 &&
        filteredLeads.every((lead) => prev.has(lead.id));
      if (allSelected) return new Set();
      return new Set(filteredLeads.map((lead) => lead.id));
    });
  }, [filteredLeads]);

  const handleLeadClick = useCallback(
    (lead: Lead) => {
      navigateShell(shellPaths.leadDetail(lead.id));
    },
    [navigateShell],
  );

  const handleLeadCreated = useCallback(
    (leadId?: string) => {
      void queryClient.invalidateQueries({ queryKey });
      void invalidateStatusBoardQueries(queryClient);
      if (!leadId) return;
      // The creator always owns the new lead, so it is guaranteed to show up
      // under Mine. Other scopes may hide it (new leads default to private).
      if (
        !leadsScopeShowsNewOwnLead({
          scope: effectiveListScope,
          leadsAlwaysShared: workspaceLeadsAlwaysShared,
        })
      ) {
        setListScope(LeadListScope.MINE);
      }
      setHighlightedLeadId(leadId);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedLeadId(null);
        highlightTimeoutRef.current = null;
      }, RESOURCE_JUST_CREATED_HIGHLIGHT_MS);
    },
    [
      effectiveListScope,
      queryClient,
      queryKey,
      setListScope,
      workspaceLeadsAlwaysShared,
    ],
  );

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = useCallback(() => {
    void refreshLeads();
  }, [refreshLeads]);

  const clearSelection = useCallback(() => {
    setSelectedLeadIds(new Set());
  }, []);

  return {
    dict,
    profile,
    profileLoading,
    currentWorkspace,
    queryKey,
    leads,
    leadsError,
    isLoading,
    searchQuery,
    setSearchQuery,
    includeArchived,
    setIncludeArchived,
    workspaceLeadsAlwaysShared,
    listSort,
    setListSort,
    sortLabels,
    leadFilters: effectiveLeadFilters,
    setLeadFilters,
    filterLabels,
    mobileFilterLabels,
    hasActiveLeadFilters,
    assigneeFilterOptions,
    tagFilterOptions,
    eventFilterOptions,
    canSeeOthersPrivate,
    listScope: effectiveListScope,
    setListScope,
    scopeCounts,
    scopeLabels: {
      mine: dict.leads.list.scope?.mine ?? 'Meine',
      workspace: dict.leads.list.scope?.workspace ?? 'Workspace',
      othersPrivate: dict.leads.list.scope?.othersPrivate ?? 'Privat anderer',
      archivedHidden: dict.leads.list.scope?.archivedHidden,
      archivedShown: dict.leads.list.scope?.archivedShown,
    },
    canExportLeads,
    canUpdateLeads,
    eventsEnabled,
    isExporting,
    filteredLeads,
    activeSelectedLeadIds,
    allFilteredSelected,
    someFilteredSelected,
    removableTagsForSelection,
    removableEventIdsForSelection,
    bulkEventOptions,
    bulkAssignmentCountsByUserId,
    bulkPrivateShareCount,
    bulkDict,
    bulkUpdateMutation,
    bulkArchiveMutation,
    toggleLeadSelect,
    toggleSelectAllFiltered,
    clearSelection,
    handleExportCsv,
    handleExportSelected,
    exportLeads: runExportLeads,
    handleLeadClick,
    handleLeadCreated,
    highlightedLeadId,
    handleRefresh,
    refreshLeads,
    resolveLeadCardMeta,
    leadCardLabelMaps,
    activeTab,
    setActiveTab,
    businessCardScanOpen,
    setBusinessCardScanOpen,
    createRequestId,
    createInitialStatus,
    requestCreateLead,
    searchRequestId,
    requestSearch,
    statusCounts,
  };
}

export type UseLeadsPageResult = ReturnType<typeof useLeadsPage>;
